import React from 'react';

interface FleetTabProps {
  vessels: any[];
  getFleetValue: (vessel: any, field: string) => any;
  setFleetField: (vessel: any, field: string, value: any) => void;
  onAdd: () => void;
  onDelete: (vessel: any) => void;
  onSaveAll: () => void;
  isAdding: boolean;
  isSaving: boolean;
}

const FleetTab: React.FC<FleetTabProps> = ({
  vessels, getFleetValue, setFleetField,
  onAdd, onDelete, onSaveAll, isAdding, isSaving,
}) => (
  <div className="space-y-6">
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
      <h3 className="font-semibold text-blue-900 mb-2">Fleet Management Settings</h3>
      <p className="text-sm text-blue-800">Configure fleet vessels and specifications</p>
    </div>

    <div className="flex justify-end mb-4">
      <button onClick={onAdd} disabled={isAdding} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
        {isAdding ? 'Adding...' : '+ Add Vessel'}
      </button>
    </div>

    <div className="space-y-4">
      {vessels.map((vessel: any) => (
        <div key={vessel.documentId || vessel.id} className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              {getFleetValue(vessel, 'icon')} {getFleetValue(vessel, 'name')}
            </h4>
            <button onClick={() => onDelete(vessel)} className="text-red-600 hover:text-red-800">🗑️ Remove</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Name</label>
              <input type="text" value={getFleetValue(vessel, 'name')} onChange={(e) => setFleetField(vessel, 'name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <input type="text" value={getFleetValue(vessel, 'type')} onChange={(e) => setFleetField(vessel, 'type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded" />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={getFleetValue(vessel, 'description')} onChange={(e) => setFleetField(vessel, 'description', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded" />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Features (comma separated)</label>
            <input
              type="text"
              value={Array.isArray(getFleetValue(vessel, 'features')) ? (getFleetValue(vessel, 'features') as string[]).join(', ') : ''}
              onChange={(e) => setFleetField(vessel, 'features', e.target.value.split(',').map((f: string) => f.trim()))}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            />
          </div>

          <div className="mt-4 flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" value={getFleetValue(vessel, 'displayOrder')} onChange={(e) => setFleetField(vessel, 'displayOrder', parseInt(e.target.value))} className="w-24 px-3 py-2 border border-gray-300 rounded" />
            </div>
            <div className="flex items-center pt-6">
              <input type="checkbox" checked={getFleetValue(vessel, 'isActive')} onChange={(e) => setFleetField(vessel, 'isActive', e.target.checked)} className="h-4 w-4 text-blue-600" />
              <label className="ml-2 text-sm text-gray-700">Active</label>
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="flex justify-end pt-4 border-t">
      <button onClick={onSaveAll} disabled={isSaving} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
        {isSaving ? 'Saving...' : 'Save Fleet Settings'}
      </button>
    </div>
  </div>
);

export default FleetTab;
